import { type UseMutationOptions, useMutation } from '@tanstack/react-query';
import {
  type DownloadPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesDownloadGetData as DownloadPdpImagesData,
  type DownloadPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesDownloadGetError as DownloadPdpImagesError,
  downloadPdpImagesV1WorkspacesWorkspaceIdPdpsPdpIdImagesDownloadGet as downloadPdpImagesZip,
} from '../../generated';

/** A zip the API streamed back, with the raw `Vizit-Content-Disposition` header that names it. */
export interface ZipDownload {
  /** `null` when the API answered `204`: none of the originals asked for are in storage. */
  blob: Blob | null;
  /** `Vizit-Content-Disposition` as sent, read it with `extractFilename` from `@vizit/utils`. */
  contentDisposition: string | null;
  /**
   * `Vizit-Total-Count`: images in the whole download when it can come in parts (a collection), so the caller knows
   * how many more parts to fetch. Absent where a download is always one zip (a PDP).
   */
  totalCount?: number | null;
}

export type DownloadPdpImagesMutationVariables = DownloadPdpImagesData['path'];

export type DownloadPdpImagesMutation = ZipDownload;

async function downloadPdpImages({
  workspace_id,
  pdp_id,
}: DownloadPdpImagesMutationVariables): Promise<DownloadPdpImagesMutation> {
  const { data, response } = await downloadPdpImagesZip({
    path: { workspace_id, pdp_id },
    parseAs: 'blob',
    throwOnError: true,
  });
  return {
    // The spec types the `204` as no body
    blob: response.status === 204 || !data ? null : data,
    contentDisposition: response.headers.get('Vizit-Content-Disposition'),
  };
}

/**
 * Downloads the hero and carousel images of a product page as one zip (SDD 3.1.2): `hero`, `carousel_1`,
 * `carousel_2`... in PDP order, originals only. The server reads the images from the PDP, nothing is sent.
 *
 * A PDP out of scope (`404 PDP_NOT_FOUND`) or without images (`404 PDP_IMAGES_NOT_FOUND`) throws, read the code with
 * `getAPIErrorCode`.
 *
 * @example
 * const { mutateAsync: downloadPdpImages } = useDownloadPdpImagesMutation();
 * const zip = await downloadPdpImages({ workspace_id, pdp_id });
 */
export const useDownloadPdpImagesMutation = <TError = DownloadPdpImagesError, TContext = unknown>(
  options?: Omit<
    UseMutationOptions<DownloadPdpImagesMutation, TError, DownloadPdpImagesMutationVariables, TContext>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  return useMutation<DownloadPdpImagesMutation, TError, DownloadPdpImagesMutationVariables, TContext>({
    mutationKey: ['DownloadPdpImages'],
    mutationFn: downloadPdpImages,
    // The zip is handed to the browser right away, no need to keep tens of MB of originals in the cache
    gcTime: 0,
    ...options,
  });
};
